/**
 * Agenda · domínio puro (sem UI, sem storage). Datas, grade do calendário,
 * cores por especialidade e o ciclo de vida do status.
 */
import type { EspecialidadeProf } from "../types";
import type { Agendamento, StatusAgendamento } from "./types";

// ── Datas (chave "YYYY-MM-DD", local, sem fuso surpresa) ─────────────────────

/** "YYYY-MM-DD" no horário LOCAL (não usa toISOString, que converte p/ UTC). */
export function chaveDia(d: Date): string {
  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
}

export function hojeChave(): string {
  return chaveDia(new Date());
}

/** Date (meia-noite local) a partir de "YYYY-MM-DD". */
export function dataDeChave(chave: string): Date {
  const [a, m, d] = chave.split("-").map(Number);
  return new Date(a, m - 1, d);
}

export const DIAS_SEMANA_CURTO = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
export const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

export function rotuloMes(ano: number, mes: number): string {
  return `${MESES[mes]} de ${ano}`;
}

/**
 * Matriz do mês para a grade do calendário: sempre 6 linhas × 7 colunas,
 * iniciando no domingo. Dias de fora do mês vêm com `noMes=false` (esmaecidos).
 */
export interface CelulaDia {
  chave: string;
  dia: number; // 1..31
  noMes: boolean;
  hoje: boolean;
}

export function matrizMes(ano: number, mes: number): CelulaDia[][] {
  const hoje = hojeChave();
  const primeiro = new Date(ano, mes, 1);
  const inicio = new Date(primeiro);
  inicio.setDate(1 - primeiro.getDay()); // recua até o domingo anterior (ou o próprio)

  const semanas: CelulaDia[][] = [];
  const cursor = new Date(inicio);
  for (let s = 0; s < 6; s++) {
    const semana: CelulaDia[] = [];
    for (let d = 0; d < 7; d++) {
      const chave = chaveDia(cursor);
      semana.push({
        chave,
        dia: cursor.getDate(),
        noMes: cursor.getMonth() === mes,
        hoje: chave === hoje,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    semanas.push(semana);
  }
  return semanas;
}

/** Adiciona meses a um (ano, mes), normalizando o estouro. */
export function somaMes(ano: number, mes: number, delta: number): { ano: number; mes: number } {
  const base = new Date(ano, mes + delta, 1);
  return { ano: base.getFullYear(), mes: base.getMonth() };
}

/** Fim de uma consulta ("HH:MM" + duração). */
export function horaFim(horaInicio: string, duracaoMin: number): string {
  const [h, m] = horaInicio.split(":").map(Number);
  const total = h * 60 + m + duracaoMin;
  const hh = String(Math.floor(total / 60) % 24).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

/** Ordena por hora de início (cronológico). */
export function porHora(a: Agendamento, b: Agendamento): number {
  return a.hora_inicio.localeCompare(b.hora_inicio);
}

/** Data legível "qua, 12 de junho". */
export function dataLegivel(chave: string): string {
  const d = dataDeChave(chave);
  return `${DIAS_SEMANA_CURTO[d.getDay()]}, ${d.getDate()} de ${MESES[d.getMonth()]}`;
}

// ── Cor do membro/disciplina no calendário ───────────────────────────────────

const COR_POR_ESP: Record<EspecialidadeProf, string> = {
  fono: "var(--disc-fono)",
  to: "var(--disc-to)",
  psico: "var(--disc-psico)",
  nutri: "var(--disc-nutri)",
  medico: "var(--disc-medico)",
  outro: "var(--disc-outro)",
};

/** Cor do profissional na agenda: a do membro, senão deriva da especialidade. */
export function corMembro(
  cor: string | null,
  especialidade: EspecialidadeProf | null,
): string {
  if (cor) return cor;
  if (especialidade) return COR_POR_ESP[especialidade];
  return "var(--text-muted)";
}

// ── Ciclo de vida do status (docs/arquitetura/09 §4) ─────────────────────────

/** Transições permitidas a partir de cada status (o que a UI oferece). */
export const TRANSICOES: Record<StatusAgendamento, StatusAgendamento[]> = {
  agendado: ["confirmado", "realizado", "faltou", "remarcado", "cancelado"],
  confirmado: ["realizado", "faltou", "remarcado", "cancelado"],
  realizado: ["remarcado"], // permite agendar um retorno a partir daqui
  faltou: ["remarcado", "cancelado"],
  remarcado: [], // terminal: gerou um novo agendamento
  cancelado: [], // terminal
};

/** Status "fechados" — não contam como compromisso ativo na agenda. */
export function ehEncerrado(status: StatusAgendamento): boolean {
  return status === "cancelado" || status === "remarcado";
}
